package net.gorillagroove.user

import kotlinx.serialization.Serializable
import net.gorillagroove.api.Api
import net.gorillagroove.api.UserId
import net.gorillagroove.db.Database
import net.gorillagroove.db.Database.userDao
import net.gorillagroove.db.DbUser
import net.gorillagroove.db.many
import net.gorillagroove.db.oneOrNull
import net.gorillagroove.localstorage.CurrentUserStore
import net.gorillagroove.localstorage.NotSignedInException
import net.gorillagroove.sync.strategies.UserResponse
import net.gorillagroove.util.EventHandlerRegistration
import net.gorillagroove.util.GGLog.logError
import net.gorillagroove.util.Settings
import net.gorillagroove.util.SettingType
import net.gorillagroove.util.TimeUtil.now

typealias UserChangeHandler = (UserChangeEvent) -> Unit

object UserService {
    fun findById(id: UserId): User? = userDao.findById(id).oneOrNull()?.toUser()

    fun findByIds(ids: List<UserId>): List<User> {
        if (ids.isEmpty()) {
            return emptyList()
        }
        return userDao.findByIds(ids).many().toUsers()
    }

    fun findExcludingIds(ids: Collection<UserId>, excludeInactive: Boolean = false, excludeHidden: Boolean = true): List<User> {
        val now = now()
        return userDao.findExcludingByIds(ids).many()
            .filter { !excludeHidden || !it.isBlocked() }
            .toUsers()
            .filter { !excludeInactive || it.hasRecentActivity(now) }
            .sortedWith(compareBy(String.CASE_INSENSITIVE_ORDER) { it.name })
    }

    fun findAll(excludeHidden: Boolean = true): List<User> {
        return userDao.findAll().many()
            .filter { !excludeHidden || !it.isBlocked() }
            .toUsers()
    }

    fun findOtherUsers(excludeInactive: Boolean = false, excludeHidden: Boolean = true): List<User> {
        val selfId = getCurrentUserId()
        return findExcludingIds(listOfNotNull(selfId), excludeInactive, excludeHidden)
    }

    fun getCurrentUserId(): UserId? {
        return CurrentUserStore.getInfo()?.id
    }

    fun requireCurrentUserId(): UserId {
        return getCurrentUserId() ?: throw NotSignedInException()
    }

    fun getCurrentUser(): User? {
        return getCurrentUserId()?.let { findById(it) }
    }

    fun requireCurrentUser(): User {
        val id = requireCurrentUserId()
        return findById(id) ?: throw IllegalStateException("No user found with ID ${id.value}!")
    }

    var hideNewUsers: Boolean
        get() = Settings.getBoolean(SettingType.HIDE_NEW_USERS, false)
        set(value) = Settings.setBoolean(SettingType.HIDE_NEW_USERS, value)

    @Throws(Throwable::class)
    suspend fun updateUserVisibility(updates: List<UserVisibilityUpdate>): List<User> {
        val request = SetUserVisibilityRequest(updates)

        val response = try {
            Api.put<List<UserResponse>>("user/visibility", request)
        } catch (e: Exception) {
            logError("Failed to update visibility for users ${updates.map { it.userId.value }}!", e)
            throw e
        }

        val updatedUsers = response.map { it.asUser() }

        Database.db.transaction {
            updatedUsers.forEach { userDao.upsert(it) }
        }

        val users = updatedUsers.toUsers()
        broadcastUserChange(users)

        Database.forceSave()

        return users
    }

    @Suppress("RedundantOverride", "unused") // Overrides needed for iOS convenience as it can't handle generic functions
    class UserChangeEventRegistration : EventHandlerRegistration<UserChangeHandler>() {
        fun registerIos(handler: UserChangeHandler) = super.register(handler)
        override fun unregister(handlerId: Int) = super.unregister(handlerId)
    }

    val eventRegistration by lazy {
        UserChangeEventRegistration()
    }

    internal fun broadcastUserChange(users: Collection<User>) {
        eventRegistration.broadcast { it(UserChangeEvent(users.toList())) }
    }
}

class UserChangeEvent(val users: List<User>)

private fun DbUser.isBlocked(): Boolean {
    return yourVisibility.asEnumeratedType() == UserVisibilityType.BLOCKED ||
        theirVisibility.asEnumeratedType() == UserVisibilityType.BLOCKED
}

@Serializable
data class UserVisibilityUpdate(val userId: UserId, val hidden: Boolean)

@Serializable
internal data class SetUserVisibilityRequest(val updates: List<UserVisibilityUpdate>)
